import { type MailRibbonControlId, type MailRibbonGroupId, type MailRibbonTabId } from "owa-ribbon-ids/lib/mailRibbonId";
import { constructKeytip, createAppToggleButton } from "owa-command-ribbon";
import { createMailAppButton, createMailAppFlyoutAnchor, createMailAppSplitButton, } from "owa-mail-ribbon-utils/lib/createMailRibbonButton";
import { getDefaultSlrAppRibbonButtonStyles, getDefaultSlrSplitButtonAppRibbonButtonStyles, } from "owa-command-ribbon-styles";
import { onChangeNoteColorMenuClicked } from "../utils/deferredOnChangeNoteColorMenuClicked";

import { onViewGroupsMenuClicked } from "../utils/deferredOnViewGroupsMenuClicked";
import { onMyDayMenuClicked } from "../utils/deferredOnMyDayMenuClicked";
import { onDeleteNoteClicked, onRestoreClicked, onRestoreAllClicked, onRibbonCustomizerClicked, onUndoClicked, onViewEmailFromNoteClicked, onViewSettingsClicked, onRemindersClicked, onExpandCollapseConversationClicked, onSyncMailboxClicked, onWorkOfflineClicked, onLightRPClicked, onNewFolderClicked, onNewSearchFolderClicked, onFolderPermissionsClicked, onToggleSelectedFolderFavorite, onSearchHighlightingControlClicked, } from "owa-mail-ribbon-command-actions/lib/commandActions";
import { openCreateGroup, openDiscoverGroups, openGroupsPanel, } from "owa-mail-ribbon-command-actions/lib/onGroupsCommandActions";
import type { ReadOnlyRibbonControlDefProps } from "owa-mail-ribbon-utils";
import type { RibbonControlProps } from "owa-acui";
import { owaComputedFn } from "owa-computed-fn";
import { getComputedCallback } from "owa-ribbon-mobx-utils/lib/getComputedCallback";
import { getControlInfo } from "owa-mail-ribbon-utils/lib/getControlInfo";
import { getDefaultRibbonStyles } from "owa-command-ribbon-styles/lib/util/getDefaultRibbonStyles";
import { getMenuDefinition } from "../../store/menuDefinitionStore";
import { onDensityMenuClicked } from "../utils/deferredOnDensityMenuClicked";
import { resetMenuDefinitionMutator } from "../../mutatorActions/menuDefinitionMutatorActions";
import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { getIsSearchTableShown } from "owa-mail-list-store";
import { onFolderMenuOptionsMenuClicked } from "../utils/deferredOnFolderMenuOptionsMenuClicked";
import { getExitSearchAction, getSearchEntityAction, getSearchRefinerAction, toggleSearchIncludeDeleted, disableSearchRefinersOnInterplay, isRefinerApplied, isSearchEntityFilterSelected, } from "../../util/getSearchRibbonActions";
import { getRibbonMailboxInfo } from "owa-mail-ribbon-utils/lib/getRibbonMailboxInfo";
import { isFolderInFavorites, isOutlookFavoritingInProgress, isPublicFolderInFavorites } from "owa-favorites";
import { isPublicFolder } from "owa-folders";
import { SEARCH_FOLDER_TYPE_NAME } from "owa-folders-constants";
import { shouldShowFavoriteToggle } from "owa-mail-folder-common";
import getSelectedFolder from "owa-mail-store/lib/utils/getSelectedFolder";
import getSelectedTableView from "owa-mail-list-store/lib/utils/getSelectedTableView";
import { getNewGroupControlState } from "owa-mail-ribbon-utils/lib/shouldShowRibbonControl";
import { newGroupDisabled_Tooltip } from "owa-mail-ribbon-utils/lib/ribbon_mail_strings.locstring.json";
import loc, { format } from "owa-localize";
import { SearchScenarioId, isSearchHitHighlightingEnabled, getSearchIncludeDeleted, } from "owa-search-store";
import BackColorPickerIcon from "owa-compose-formatcontrols/lib/fonts/BackColorPickerIcon";
import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";
import { getFromRefinerSelections } from "owa-mail-search";
import { fromRefiner_SelectedPerson, fromRefiner_SelectedCount, } from "../FromRefinerFlyoutContent.locstring.json";
import { fromRefinerToggledButton } from "../FromRefinerFlyoutContent.scss";
import MailOpenPersonFilled from "owa-fluent-icons-svg/lib/icons/MailOpenPersonFilled";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { onFromRefinerClicked } from "../utils/deferredOnFromRefinerClicked";

const noOp = (): void => {
    /* Sentinel controls are replaced by the custom in-menu renderer. */
};

/**
 * This file is for shared components in read-only scenarios. Meaning, any tab or control that only shows up
 * when a user is composing. In this case, Home tab, View tab, and Notes tab are good examples.
 *
 * For controls that show up in compose-only scenarios, please use composeSharedControlDefinitions.ts.
 */

export const getDeleteNoteControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getDeleteNoteControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const controlInfo = getControlInfo(640, props);
    return createMailAppButton(640, controlInfo.text, controlInfo.tooltip, props, getComputedCallback(640, onDeleteNoteClicked), constructKeytip([6], 640), controlInfo.iconName, undefined /*iconColor*/, getDefaultSlrSplitButtonAppRibbonButtonStyles());
});

export const getAddToFavoritesControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getAddToFavoritesControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(767, props);
    const selectedFolder = getSelectedFolder();
    const folderId = selectedFolder?.id;
    const isFavorite = folderId ? isFolderFavorited(folderId) : false;
    const isFavoriting = folderId ? isOutlookFavoritingInProgress(folderId) : false;
    // Public folders are added through the picker; this control can only remove a selected favorite.
    const canToggleFavorite = !!folderId &&
        shouldShowFavoriteToggle(mailboxInfo, getSelectedTableView()) &&
        (!isPublicFolder(folderId) || isFavorite);
    
    return createAppToggleButton(767, controlInfo.text, getComputedCallback(767, onToggleSelectedFolderFavorite, mailboxInfo), mailboxInfo, isFavorite, constructKeytip([17], 767), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles(), {
        customTooltip: controlInfo.tooltip,
        disabled: !canToggleFavorite || isFavoriting,
    });
});

function isFolderFavorited(folderId: string): boolean {
    return isPublicFolder(folderId)
        ? isPublicFolderInFavorites(folderId)
        : isFolderInFavorites(folderId);
}

export const getChangeNoteColorControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getChangeNoteColorControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(641, props);
    const storeName = "ChangeNoteColorMenuDefinitionStore";
    return createMailAppFlyoutAnchor(641, controlInfo.text, controlInfo.tooltip, props, getMenuDefinition(storeName), mailboxInfo, constructKeytip([6], 641, true /* hasMenu */), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles(), undefined /* itemId */, {
        onExecute: () => {
            onChangeNoteColorMenuClicked(props);
        },
        onAfterMenuDismiss: () => {
            resetMenuDefinitionMutator(storeName);
        },
    });
});

export const getNotesViewEmailControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getNotesViewEmailControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const controlInfo = getControlInfo(642, props);
    return createMailAppButton(642, controlInfo.text, controlInfo.tooltip, props, getComputedCallback(642, onViewEmailFromNoteClicked), constructKeytip([6], 642), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles());
});

/**
 * Gets the Has Attachment refiner control definition
 */
export const getHasAttachmentRefinerControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getHasAttachmentRefinerControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(741, props);
    
    return createAppToggleButton(741, controlInfo.text, getComputedCallback(741, () => getSearchRefinerAction("HasAttachment")), mailboxInfo, isRefinerApplied("HasAttachment"), constructKeytip([14], 741), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles(), { disabled: disableSearchRefinersOnInterplay() });
});

/**
 * Gets the Is Unread refiner control definition
 */
export const getIsUnreadRefinerControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getIsUnreadRefinerControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(742, props);
    
    return createAppToggleButton(742, controlInfo.text, getComputedCallback(742, () => getSearchRefinerAction("IsUnread")), mailboxInfo, isRefinerApplied("IsUnread"), constructKeytip([14], 742), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles(), { disabled: disableSearchRefinersOnInterplay() });
});

/**
 * Gets the Is Mentioned refiner control definition
 */
export const getIsMentionedRefinerControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getIsMentionedRefinerControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(743, props);
    
    return createAppToggleButton(743, controlInfo.text, getComputedCallback(743, () => getSearchRefinerAction("IsMentioned")), mailboxInfo, isRefinerApplied("IsMentioned"), constructKeytip([14], 743), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles(), { disabled: disableSearchRefinersOnInterplay() });
});

/**
 * Gets the Is Mentioned In Chats refiner control definition
 */
export const getIsMentionedInChatsRefinerControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getIsMentionedInChatsRefinerControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(744, props);
    
    return createAppToggleButton(744, controlInfo.text, getComputedCallback(744, () => getSearchRefinerAction("IsMentionedInChats")), mailboxInfo, isRefinerApplied("IsMentionedInChats"), constructKeytip([14], 744), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles(), { disabled: disableSearchRefinersOnInterplay() });
});

/**
 * Gets the Is Flagged refiner control definition
 */
export const getIsFlaggedRefinerControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getIsFlaggedRefinerControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(745, props);
    
    return createAppToggleButton(745, controlInfo.text, getComputedCallback(745, () => getSearchRefinerAction("IsFlagged")), mailboxInfo, isRefinerApplied("IsFlagged"), constructKeytip([14], 745), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles(), { disabled: disableSearchRefinersOnInterplay() });
});

/**
 * Gets the High Importance refiner control definition
 */
export const getHighImportanceRefinerControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getHighImportanceRefinerControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(746, props);
    
    return createAppToggleButton(746, controlInfo.text, getComputedCallback(746, () => getSearchRefinerAction("HighImportance")), mailboxInfo, isRefinerApplied("HighImportance"), constructKeytip([14], 746), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles(), { disabled: disableSearchRefinersOnInterplay() });
});

export const getFromRefinerControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getFromRefinerControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const controlInfo = getControlInfo(771, props);
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const selectedPeople = getFromRefinerSelections();
    const isToggled = selectedPeople.length > 0;
    const selectedNames = selectedPeople.map(person => person.displayName);
    const anchorLabel = isToggled
        ? format(loc(fromRefiner_SelectedCount), selectedNames.length)
        : controlInfo.text;
    const selectedPeopleLabel = isToggled
        ? format(loc(fromRefiner_SelectedPerson), selectedNames.join(", "))
        : undefined;
    const iconName = isToggled ? MailOpenPersonFilled : controlInfo.iconName;
    const sentinelItem = createAppToggleButton(771, controlInfo.text, noOp, mailboxInfo, false, constructKeytip([14, 771], 771), controlInfo.iconName);
    
    return createMailAppFlyoutAnchor(771, anchorLabel, selectedPeopleLabel ?? controlInfo.tooltip, props, { sections: [{ controls: [sentinelItem] }] }, mailboxInfo, constructKeytip([14], 771, true), iconName, undefined /*iconColor*/, getDefaultRibbonStyles(), undefined /*itemId*/, {
        onExecute: onFromRefinerClicked,
        disabled: disableSearchRefinersOnInterplay(),
        shouldFocusOnMount: false,
        shouldFocusOnContainer: false,
        toggled: isToggled,
        hideLabel: false,
        ...(selectedPeopleLabel && {
            ariaLabel: selectedPeopleLabel,
            fullDescriptionLabel: selectedPeopleLabel,
            labelInMenu: selectedPeopleLabel,
            title: selectedPeopleLabel,
        }),
        ...(isGlobalFeatureEnabled("mon-ribbon-fluent-v9-ribbon") &&
            isToggled && {
            menuButton: { className: fromRefinerToggledButton },
        }),
    });
});

export const getToRefinerControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getToRefinerControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const controlInfo = getControlInfo(773, props);
    
    return createMailAppButton(773, controlInfo.text, controlInfo.tooltip, props, 
    // The To refiner is not interactive because the feature is in development
    () => undefined, constructKeytip([14], 773), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles(), undefined /*itemId*/, { disabled: true });
});

/**
 * Gets the To Me refiner control definition
 */
export const getToMeRefinerControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getToMeRefinerControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(747, props);
    
    return createAppToggleButton(747, controlInfo.text, getComputedCallback(747, () => getSearchRefinerAction("ToMe")), mailboxInfo, isRefinerApplied("ToMe"), constructKeytip([14], 747), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles(), { disabled: disableSearchRefinersOnInterplay() });
});

/**
 * Gets the Date Range refiner control definition
 */
export const getDateRangeRefinerControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getDateRangeRefinerControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const controlInfo = getControlInfo(748, props);
    return createMailAppButton(748, controlInfo.text, controlInfo.tooltip, props, () => {
        // TODO: Implement date range refiner action
    }, constructKeytip([14], 748), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles(), undefined /*itemId*/);
});

/**
 * Gets the Advanced Search control definition
 */
export const getAdvancedSearchControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getAdvancedSearchControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const controlInfo = getControlInfo(739, props);
    return createMailAppButton(739, controlInfo.text, controlInfo.tooltip, props, () => {
        // TODO: Implement advanced search action
    }, constructKeytip([14], 739), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles(), undefined /*itemId*/);
});

/**
 * Gets the Clear Search control definition
 */
export const getCloseSearchControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getCloseSearchControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const controlInfo = getControlInfo(740, props);
    return createMailAppButton(740, controlInfo.text, controlInfo.tooltip, props, getComputedCallback(740, () => getExitSearchAction()), constructKeytip([14], 740), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles(), undefined /*itemId*/, { disabled: disableSearchRefinersOnInterplay() });
});

/**
 * Gets the Search Highlighting control definition
 */
export const getSearchHighlightingControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getSearchHighlightingControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(766, props);
    const isEnabled = isSearchHitHighlightingEnabled(SearchScenarioId.Mail);
    
    return createAppToggleButton(766, controlInfo.text, getComputedCallback(766, onSearchHighlightingControlClicked, mailboxInfo, !isEnabled), mailboxInfo, isEnabled, constructKeytip([14], 766), BackColorPickerIcon, undefined /*iconColor*/, getDefaultRibbonStyles(), { customTooltip: controlInfo.tooltip });
});

/**
 * Gets the Include Deleted search control definition
 */
export const getIncludeDeletedControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getIncludeDeletedControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(770, props);
    return createAppToggleButton(770, controlInfo.text, toggleSearchIncludeDeleted, mailboxInfo, getSearchIncludeDeleted(mailboxInfo), constructKeytip([14], 770), controlInfo.iconName, undefined /* iconColor */, getDefaultRibbonStyles(), {
        customTooltip: controlInfo.tooltip,
        disabled: disableSearchRefinersOnInterplay(),
    });
});

/**
 * Gets the Include Channels refiner control definition
 */ export const getIncludeChannelsRefinerControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getIncludeChannelsRefinerControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(749, props);
    
    return createAppToggleButton(749, controlInfo.text, getComputedCallback(749, () => getSearchRefinerAction("IncludeChannels")), mailboxInfo, isRefinerApplied("IncludeChannels"), constructKeytip([14], 749), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles());
});

/**
 * Gets the Entity All control definition
 */
export const getEntityAllControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getEntityAllControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(754, props);
    
    return createAppToggleButton(754, controlInfo.text, async () => {
        getSearchEntityAction("All");
    }, mailboxInfo, isSearchEntityFilterSelected("All"), constructKeytip([14], 754), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles(), { disabled: disableSearchRefinersOnInterplay() });
});

/**
 * Gets the Entity Mail control definition
 */
export const getEntityMailControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getEntityMailControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(750, props);
    
    return createAppToggleButton(750, controlInfo.text, async () => {
        getSearchEntityAction("Mail");
    }, mailboxInfo, isSearchEntityFilterSelected("Mail"), constructKeytip([14], 750), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles(), { disabled: disableSearchRefinersOnInterplay() });
});

/**
 * Gets the Entity Chats control definition
 */
export const getEntityChatsControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getEntityChatsControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(751, props);
    
    return createAppToggleButton(751, controlInfo.text, async () => {
        getSearchEntityAction("Chats");
    }, mailboxInfo, isSearchEntityFilterSelected("Chats"), constructKeytip([14], 751), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles(), { disabled: disableSearchRefinersOnInterplay() });
});

/**
 * Gets the Entity Files control definition
 */
export const getEntityFilesControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getEntityFilesControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(752, props);
    
    return createAppToggleButton(752, controlInfo.text, async () => {
        getSearchEntityAction("Files");
    }, mailboxInfo, isSearchEntityFilterSelected("Files"), constructKeytip([14], 752), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles(), { disabled: disableSearchRefinersOnInterplay() });
});

/**
 * Gets the Entity People control definition
 */
export const getEntityPeopleControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getEntityPeopleControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(753, props);
    
    return createAppToggleButton(753, controlInfo.text, async () => {
        getSearchEntityAction("People");
    }, mailboxInfo, isSearchEntityFilterSelected("People"), constructKeytip([14], 753), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles(), { disabled: disableSearchRefinersOnInterplay() });
});

export const getRibbonCustomizerControl: (tabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getRibbonCustomizerControl(tabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const controlInfo = getControlInfo(558, props);
    const mailboxInfo = props.mailboxInfo ?? getGlobalSettingsAccountMailboxInfo();
    return createMailAppButton(558, controlInfo.text, controlInfo.tooltip, props, getComputedCallback(558, onRibbonCustomizerClicked, tabId, mailboxInfo), constructKeytip([tabId], 558), undefined /* icon */, undefined /* iconColor */, getDefaultSlrAppRibbonButtonStyles(), undefined /* itemId */, { disabled: isWin32StylingEnabled() });
});

export const getDensityControl: (isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getDensityControl(isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(649, props);
    const storeName = "DensityMenuDefinitionStore";
    return createMailAppFlyoutAnchor(649, controlInfo.text, controlInfo.tooltip, props, getMenuDefinition(storeName), mailboxInfo, constructKeytip([keytipTabId], 649, true /* hasMenu */), controlInfo.iconName, undefined /* iconColor */, isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined, undefined /* itemId */, {
        onExecute: () => {
            onDensityMenuClicked(mailboxInfo);
        },
        onAfterMenuDismiss: () => {
            resetMenuDefinitionMutator(storeName);
        },
    });
});

export const getViewSettingsControl: (isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getViewSettingsControl(isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const controlInfo = getControlInfo(643, props);
    return createMailAppButton(643, controlInfo.text, controlInfo.tooltip, props, getComputedCallback(643, onViewSettingsClicked, props), constructKeytip([keytipTabId], 643), controlInfo.iconName, undefined /* iconColor */, isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined);
});

export const getDiscoverGroupsControl: (keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getDiscoverGroupsControl(keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const controlInfo = getControlInfo(508, props);
    return createMailAppButton(508, controlInfo.text, controlInfo.tooltip, props, getComputedCallback(508, openDiscoverGroups, props), constructKeytip([keytipTabId], 508), controlInfo.iconName);
});

export const getNewGroupControl: (keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getNewGroupControl(keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const controlInfo = getControlInfo(585, props);
    const { disabled } = getNewGroupControlState(getRibbonMailboxInfo(props.mailboxInfo));
    return createMailAppButton(585, controlInfo.text, disabled ? loc(newGroupDisabled_Tooltip) : controlInfo.tooltip, props, getComputedCallback(585, openCreateGroup, props), constructKeytip([keytipTabId], 109), controlInfo.iconName, undefined /* iconColor */, undefined /* styles */, undefined /* itemId */, { disabled });
});

export const getViewGroupsPanelControl: (isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getViewGroupsPanelControl(isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const controlInfo = getControlInfo(663, props);
    const storeName = "ViewGroupsMenuDefinitionStore";
    
    return createMailAppSplitButton(663, controlInfo.text, controlInfo.ariaText, controlInfo.tooltip, props, getMenuDefinition(storeName), getComputedCallback(663, openGroupsPanel), constructKeytip([keytipTabId], 663, true /* hasMenu */), controlInfo.iconName, undefined /*iconColor*/, isSLR ? getDefaultSlrSplitButtonAppRibbonButtonStyles() : undefined, undefined /*itemId*/, {
        onMenuButtonExecute: () => {
            onViewGroupsMenuClicked(keytipTabId, props);
        },
        onAfterMenuDismiss: () => {
            resetMenuDefinitionMutator(storeName);
        },
    });
});

export const getUndoControl: (isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getUndoControl(isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const controlInfo = getControlInfo(580, props);
    return createMailAppButton(580, controlInfo.text, controlInfo.tooltip, props, getComputedCallback(580, onUndoClicked), constructKeytip([keytipTabId], 580), controlInfo.iconName, undefined /*iconColor*/, isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined);
});

export const getRestoreControl: (isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getRestoreControl(isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const controlInfo = getControlInfo(683, props);
    return createMailAppButton(683, controlInfo.text, controlInfo.tooltip, props, getComputedCallback(683, onRestoreClicked), constructKeytip([keytipTabId], 683), controlInfo.iconName, undefined /*iconColor*/, isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined);
});

export const getRestoreAllControl: (isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getRestoreAllControl(isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const controlInfo = getControlInfo(684, props);
    return createMailAppButton(684, controlInfo.text, controlInfo.tooltip, props, getComputedCallback(684, onRestoreAllClicked), constructKeytip([keytipTabId], 684), controlInfo.iconName, undefined /*iconColor*/, isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined);
});

export const getRemindersWindowControl: (isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getRemindesWindowControl(isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(690, props);
    return createMailAppButton(690, controlInfo.text, controlInfo.tooltip, props, getComputedCallback(690, onRemindersClicked, mailboxInfo), constructKeytip([keytipTabId], 690), controlInfo.iconName, undefined /* iconColor */, isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined, undefined /* itemId */);
});
export const getExpandCollapseConversationControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getExpandCollapseConversationControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const controlInfo = getControlInfo(687, props);
    return createMailAppButton(687, controlInfo.text, controlInfo.tooltip, props, getComputedCallback(687, onExpandCollapseConversationClicked, props.itemId?.Id ?? "", getRibbonMailboxInfo(props.mailboxInfo)), constructKeytip([3], 687, true /* hasMenu */), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles());
});

export const getLightRPControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getLightRPControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const controlInfo = getControlInfo(720, props);
    return createMailAppButton(720, controlInfo.text, controlInfo.tooltip, props, getComputedCallback(720, onLightRPClicked), constructKeytip([3], 720), controlInfo.iconName, undefined /*iconColor*/, getDefaultRibbonStyles());
});

export const getSyncMailboxControl: (isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getSyncMailboxControl(isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(701, props);
    return createMailAppButton(701, controlInfo.text, controlInfo.tooltip, props, getComputedCallback(701, onSyncMailboxClicked, mailboxInfo), constructKeytip([keytipTabId], 701), controlInfo.iconName, undefined, isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined, undefined /*itemId*/, {
        disabled: !props.mailboxInfo || getIsSearchTableShown(),
    });
});

export const getSendReceiveAllFoldersControl: (isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getSendReceiveAllFoldersControl(isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(562, props);
    return createMailAppButton(562, controlInfo.text, controlInfo.tooltip, props, getComputedCallback(562, onSyncMailboxClicked, mailboxInfo), constructKeytip([keytipTabId], 562), controlInfo.iconName, undefined /* iconColor */, isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined, undefined /*itemId*/, {
        disabled: !props.mailboxInfo || getIsSearchTableShown(),
    });
});

export const getWorkOfflineControl: (isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getWorkOfflineControl(isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(544, props);
    return createMailAppButton(544, controlInfo.text, controlInfo.tooltip, props, getComputedCallback(544, onWorkOfflineClicked, mailboxInfo), constructKeytip([keytipTabId], 544), controlInfo.iconName, undefined /* iconColor */, isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined);
});

export const getMyDayControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getMyDayControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(706, props);
    const storeName = "MyDayOptionsMenuDefinitionStore";
    return createMailAppFlyoutAnchor(706, controlInfo.text, controlInfo.tooltip, props, getMenuDefinition(storeName), mailboxInfo, constructKeytip([3], 706, true /* hasMenu */), controlInfo.iconName, undefined /* iconColor */, undefined /* styles */, undefined /* itemId */, {
        onExecute: () => {
            onMyDayMenuClicked(mailboxInfo);
        },
        onAfterMenuDismiss: () => {
            resetMenuDefinitionMutator(storeName);
        },
    });
});

export const getFolderPaneControl: (isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getFolderPaneControl(isSLR: boolean, keytipTabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(647, props);
    const storeName = "FolderMenuOptionsMenuDefinitionStore";
    return createMailAppFlyoutAnchor(647, controlInfo.text, controlInfo.tooltip, props, getMenuDefinition(storeName), mailboxInfo, constructKeytip([keytipTabId], 647, true /* hasMenu */), controlInfo.iconName, undefined /* iconColor */, isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined, undefined /* itemId */, {
        onExecute: () => {
            onFolderMenuOptionsMenuClicked(props, mailboxInfo, keytipTabId);
        },
        onAfterMenuDismiss: () => {
            resetMenuDefinitionMutator(storeName);
        },
    });
});

export const getNewFolderControl: (isSLR: boolean, props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getNewFolderControl(isSLR: boolean, props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(768, props);
    return createMailAppButton(768, controlInfo.text, controlInfo.tooltip, props, getComputedCallback(768, onNewFolderClicked, mailboxInfo), constructKeytip([17], 768), controlInfo.iconName, undefined /* iconColor */, isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined);
});

export const getNewSearchFolderControl: (isSLR: boolean, props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getNewSearchFolderControl(isSLR: boolean, props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getControlInfo(769, props);
    return createMailAppButton(769, controlInfo.text, controlInfo.tooltip, props, getComputedCallback(769, onNewSearchFolderClicked, mailboxInfo), constructKeytip([17], 769), controlInfo.iconName, undefined /* iconColor */, isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined);
});

export const getFolderPermissionsControl: (props: ReadOnlyRibbonControlDefProps) => RibbonControlProps = owaComputedFn(function getFolderPermissionsControl(props: ReadOnlyRibbonControlDefProps): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const selectedFolder = getSelectedFolder();
    const folderId = selectedFolder?.id;
    const isSearchFolder = selectedFolder?.type === SEARCH_FOLDER_TYPE_NAME;
    const controlInfo = getControlInfo(772, props);
    
    return createMailAppButton(772, controlInfo.text, controlInfo.tooltip, props, getComputedCallback(772, onFolderPermissionsClicked, folderId, mailboxInfo), constructKeytip([17], 772), controlInfo.iconName, undefined /* iconColor */, undefined /* styles */, undefined /* itemId */, { disabled: !folderId || isSearchFolder });
});
